import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useProducts,
  useDeleteProduct,
  useBulkDeleteProducts,
  useBulkUpdateProducts,
  usePricebooks,
} from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { Package, Plus, CheckCircle2, XCircle, AlertCircle, Download, Tag } from 'lucide-react'
import { exportProductsToCSV } from '@/lib/csv-export'
import type { Product as _Product } from '@/types'
import { ProductDetailModal } from '@/components/ProductDetailModal'
import { CreateProductModal } from '@/components/CreateProductModal'
import { EditProductModal } from '@/components/EditProductModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import {
  createProductsColumns,
  productsDefaultColumnSizing,
} from '@/config/columns/products-columns'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/contexts/UserContext'
import { explainError } from '@/lib/explain-error'

export default function Products() {
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<{ id: string; name: string } | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)

  const { isAdmin, impersonatedUser } = useUser()
  const navigate = useNavigate()

  // The single standard price list, so admins can jump straight to it from the
  // catalogue rather than navigating Pricebooks → book → Manage Prices.
  const { data: pricebooksData } = usePricebooks({ is_standard: true, limit: 1 })
  const standardPricebookId = pricebooksData?.data?.[0]?.id

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))

  const { data, isLoading } = useProducts({ search, page, sort_by: sortBy, sort_order: sortOrder })

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )
  const deleteMutation = useDeleteProduct()
  const bulkDeleteMutation = useBulkDeleteProducts()
  const bulkUpdateMutation = useBulkUpdateProducts()
  const { toast } = useToast()

  const {
    items: products,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
    stats,
  } = extract(data)

  // Use stats from API if available, otherwise fallback to 0
  const totalProducts = stats?.totalProducts ?? total
  const activeProducts = stats?.activeProducts ?? 0
  const inactiveProducts = stats?.inactiveProducts ?? 0
  // Products with no price in ANY selling currency — a catalogue defect.
  // Not "no CAD price", which is a coverage question and belongs on the price
  // list where every currency is visible at once.
  const productsWithoutPricing = stats?.productsWithoutPricing ?? 0

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of products
    // In a production app, you might want to fetch all products from the API
    exportProductsToCSV(products)
    toast({
      title: 'Export Successful',
      description: `Exported ${products.length} products to CSV`,
    })
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!productToDelete) return

    try {
      await deleteMutation.mutateAsync(productToDelete.id)

      toast({
        title: 'Success',
        description: `Product "${productToDelete.name}" has been deleted.`,
      })

      setDeleteDialogOpen(false)
      setProductToDelete(null)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete product')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(products.map((product) => product.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} product${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete products')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      // Convert is_active string value to boolean if present
      const processedUpdates = { ...updates }
      if (processedUpdates.is_active !== undefined) {
        processedUpdates.is_active = processedUpdates.is_active === 'true'
      }

      await bulkUpdateMutation.mutateAsync({
        ids: Array.from(selectedIds),
        updates: processedUpdates,
      })

      toast({
        title: 'Success',
        description: `${selectedIds.size} product${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update products')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Define field configurations
  const productsUpdateFields: BulkUpdateField[] = [
    {
      key: 'is_active',
      label: 'Active',
      type: 'select',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
    {
      key: 'product_family',
      label: 'Product Family',
      type: 'text',
      placeholder: 'Enter product family',
    },
  ]

  // Create columns with callbacks
  const columns = useMemo(
    () =>
      createProductsColumns({
        onViewProduct: (productId: string) => {
          setSelectedProductId(productId)
          setDetailModalOpen(true)
        },
        onEditProduct: (productId: string) => {
          setSelectedProductId(productId)
          setEditModalOpen(true)
        },
        onDeleteProduct: (productId: string, name: string) => {
          setProductToDelete({ id: productId, name })
          setDeleteDialogOpen(true)
        },
        canEdit: isAdmin,
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === products.length && products.length > 0,
      }),
    [selectedIds, products.length, isAdmin]
  )

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="product"
          onClearSelection={handleClearSelection}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="product" />
            <h1 className="text-3xl font-bold tracking-tight">Products</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage your product catalog</p>
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['products']} label="products" />
          {canExport && (
            <Button variant="outline" aria-label="Export products to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          {/* The price list was reachable only via Pricebooks → a pricebook →
              Manage Prices — three levels deep from the catalogue it prices.
              Admins get a direct route to it from here. */}
          {isAdmin && standardPricebookId && (
            <Button
              variant="outline"
              aria-label="Open the price list"
              onClick={() => navigate(`/pricebooks/${standardPricebookId}/prices`)}
            >
              <Tag className="mr-2 h-4 w-4" aria-hidden="true" />
              Price List
            </Button>
          )}
          <Button aria-label="Create new product" onClick={() => setCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Product
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <Package className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{totalProducts}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {products.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Active Products</CardTitle>
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{activeProducts}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Inactive Products</CardTitle>
            <XCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{inactiveProducts}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">No Selling Price</CardTitle>
            <AlertCircle className="h-3.5 w-3.5 text-warning" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none text-warning">
              {productsWithoutPricing}
            </div>
            <p className="text-xs text-muted-foreground leading-tight">
              Not priced in any selling currency
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Products</CardTitle>
          <CardDescription>View and manage your product catalog</CardDescription>
          <SearchWithRecent
            value={search}
            onChange={setSearch}
            placeholder="Search products..."
            ariaLabel="Search products"
            entityKey="products"
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No products found. Create your first product to get started.
            </div>
          ) : (
            <ResizableTable
              columns={columns}
              data={products}
              storageKey="products-column-widths"
              defaultColumnSizing={productsDefaultColumnSizing}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
                {total} products
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                  Previous
                </Button>
                <div className="flex items-center gap-1 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ProductDetailModal
        productId={selectedProductId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        canEdit={isAdmin}
      />

      <CreateProductModal open={createModalOpen} onOpenChange={setCreateModalOpen} />

      <EditProductModal
        productId={selectedProductId}
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-product-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription id="delete-product-description">
              Are you sure you want to delete the product "{productToDelete?.name}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete product ${productToDelete?.name}`}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="product"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="product"
        fields={productsUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />
    </div>
  )
}
